import { useState } from 'react';
import {
  JERSEY_SKU_KINDS,
  emptyJerseyConfig,
  jerseyStarterConfig,
  type JerseyIdentifierConfig,
  type JerseySkuKind,
  type JerseyToken,
} from '@klothiq/domain';
import { Input, Select } from '@/components/ui/FormControls';
import FieldHint from '@/components/common/FieldHint';

export default function JerseyIdentifierSettings({
  value,
  onChange,
  teams,
  available,
  optionalEntry = false,
}: {
  value?: JerseyIdentifierConfig;
  onChange: (value: JerseyIdentifierConfig) => void;
  teams: { id: string; name: string; code: string }[];
  available: boolean;
  optionalEntry?: boolean;
}) {
  const config = value || emptyJerseyConfig();
  const [kind, setKind] = useState<JerseySkuKind>('collection');
  const updateToken = (index: number, patch: Partial<JerseyToken>) =>
    onChange({
      ...config,
      tokens: {
        ...config.tokens,
        [kind]: config.tokens[kind].map((t, i) => (i === index ? { ...t, ...patch } : t)),
      },
    });
  return (
    <section className="glass-card space-y-4 p-4 sm:p-6">
      <div>
        <h2 className="font-semibold">Jersey product codes and SKUs</h2>
        <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
          {optionalEntry
            ? 'Mappings for future optional jersey suggestions. Missing details use the configured neutral token; existing identifiers are kept.'
            : 'Optional, only for this category. Uses TEAM-number codes and detailed SKUs. Product names, existing identifiers and the entry workflow are kept.'}
        </p>
      </div>
      {!available && (
        <p role="status" className="text-sm text-amber-600">
          Apply the jersey identifier migration before enabling this setting.
        </p>
      )}
      <label className="flex min-h-11 items-center gap-3 text-sm">
        <Input
          type="checkbox"
          disabled={!available || optionalEntry}
          checked={config.enabled}
          onChange={(e) => onChange({ ...config, enabled: e.target.checked })}
          helpLabel="Enable jersey identifiers for this category"
        />
        Enable the previous required-review identifier flow (kept off for optional entry)
      </label>
      <button
        type="button"
        className="min-h-11 text-sm text-primary underline"
        onClick={() => onChange(jerseyStarterConfig(config))}
      >
        Add starter mappings to empty sections for review
      </button>
      {(config.enabled ||
        config.teams.length > 0 ||
        JERSEY_SKU_KINDS.some((k) => config.tokens[k].length)) && (
        <>
          <p className="text-xs text-muted-foreground">
            {optionalEntry
              ? 'Review mappings before publishing. Team and all descriptive fields may be left blank during product entry; no Unspecified selection is required.'
              : 'Codes are reserved permanently. Review every mapping before publishing. Unknown details need an explicit Unspecified entry; blank values are never guessed.'}
          </p>
          <h3 className="text-sm font-semibold">Team abbreviations</h3>
          {config.teams.map((team, index) => (
            <div
              key={index}
              className="grid gap-2 rounded-xl border border-border p-3 sm:grid-cols-3"
            >
              <label className="space-y-1 text-xs">
                Team
                <Select
                  className="w-full"
                  value={team.team_id}
                  helpLabel="Jersey identifier team mapping"
                  onChange={(e) =>
                    onChange({
                      ...config,
                      teams: config.teams.map((t, i) =>
                        i === index ? { ...t, team_id: e.target.value } : t,
                      ),
                    })
                  }
                >
                  <option value="">Choose team</option>
                  {teams.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.name}
                    </option>
                  ))}
                </Select>
              </label>
              <label className="space-y-1 text-xs">
                Short code
                <Input
                  value={team.code}
                  helpLabel="Jersey team short code"
                  help="Use 2–8 uppercase letters or numbers. Issued product codes remain permanent."
                  maxLength={8}
                  placeholder="BAR or RM"
                  onChange={(e) =>
                    onChange({
                      ...config,
                      teams: config.teams.map((t, i) =>
                        i === index ? { ...t, code: e.target.value.toUpperCase() } : t,
                      ),
                    })
                  }
                />
              </label>
              <label className="space-y-1 text-xs">
                Aliases (separate with ;)
                <Input
                  value={team.aliases.join(';')}
                  helpLabel="Jersey team aliases"
                  help="Separate reviewed alternate team spellings with semicolons. No fuzzy matches are used."
                  onChange={(e) =>
                    onChange({
                      ...config,
                      teams: config.teams.map((t, i) =>
                        i === index ? { ...t, aliases: e.target.value.split(';') } : t,
                      ),
                    })
                  }
                />
              </label>
              <button
                type="button"
                className="min-h-11 text-left text-xs text-muted-foreground underline"
                onClick={() =>
                  onChange({ ...config, teams: config.teams.filter((_, i) => i !== index) })
                }
              >
                Remove from this version
              </button>
            </div>
          ))}
          <button
            type="button"
            className="min-h-11 text-sm text-primary"
            onClick={() =>
              onChange({
                ...config,
                teams: [...config.teams, { team_id: '', code: '', aliases: [] }],
              })
            }
          >
            + Add team mapping
          </button>
          <label className="block space-y-1 text-sm">
            SKU segment
            <Select
              className="w-full sm:max-w-xs"
              value={kind}
              helpLabel="Jersey SKU segment to configure"
              onChange={(e) => setKind(e.target.value as JerseySkuKind)}
            >
              {JERSEY_SKU_KINDS.map((k) => (
                <option key={k} value={k}>
                  {k.charAt(0).toUpperCase() + k.slice(1)}
                </option>
              ))}
            </Select>
          </label>
          <FieldHint>
            Stable keys identify values. Labels and aliases match current inputs exactly, including
            configured multicolours and size aliases such as 2XL. Tokens are uppercase
            letters/numbers, without hyphens. Deactivate used entries instead of replacing their
            meaning.
          </FieldHint>
          {config.tokens[kind].map((entry, index) => (
            <div key={`${kind}-${index}`} className="space-y-3 rounded-xl border border-border p-3">
              <div className="grid gap-3 sm:grid-cols-2">
                <label className="space-y-1 text-xs">
                  Stable key
                  <Input
                    value={entry.key}
                    helpLabel={`Stable ${kind} key`}
                    help="Keep this key stable for the same meaning across published versions."
                    maxLength={64}
                    onChange={(e) => updateToken(index, { key: e.target.value })}
                  />
                </label>
                <label className="space-y-1 text-xs">
                  Label
                  <Input
                    value={entry.label}
                    helpLabel={`${kind} display label`}
                    maxLength={100}
                    onChange={(e) => updateToken(index, { label: e.target.value })}
                  />
                </label>
                <label className="space-y-1 text-xs">
                  SKU token
                  <Input
                    value={entry.token}
                    helpLabel={`${kind} SKU token`}
                    help="Uppercase letters and numbers only. Retired tokens cannot be reused for a different meaning."
                    maxLength={kind === 'print' || kind === 'edition' ? 16 : 12}
                    placeholder={kind === 'colour' ? 'BKV' : kind === 'print' ? 'LY10' : 'UNK'}
                    onChange={(e) => updateToken(index, { token: e.target.value.toUpperCase() })}
                  />
                </label>
                <label className="space-y-1 text-xs">
                  Aliases (separate with ;)
                  <Input
                    value={entry.aliases.join(';')}
                    helpLabel={`${kind} aliases`}
                    help="Separate exact alternate spellings with semicolons, e.g. 2XL as an alias for XXL."
                    onChange={(e) => updateToken(index, { aliases: e.target.value.split(';') })}
                  />
                </label>
                {kind === 'print' && (
                  <>
                    <label className="space-y-1 text-xs">
                      Print kind
                      <Select
                        className="w-full"
                        value={entry.print_kind || ''}
                        helpLabel="Jersey print kind"
                        onChange={(e) =>
                          updateToken(index, {
                            print_kind: e.target.value as JerseyToken['print_kind'],
                            player_name: '',
                            shirt_number: null,
                          })
                        }
                      >
                        <option value="">Choose kind</option>
                        <option value="no_name">No Name</option>
                        <option value="named_player">Named player</option>
                        <option value="customisable">Customisable</option>
                        <option value="unspecified">Unspecified</option>
                      </Select>
                    </label>
                    {entry.print_kind === 'named_player' && (
                      <>
                        <label className="space-y-1 text-xs">
                          Player name
                          <Input
                            value={entry.player_name || ''}
                            helpLabel="Configured jersey player name"
                            maxLength={100}
                            onChange={(e) => updateToken(index, { player_name: e.target.value })}
                          />
                        </label>
                        <label className="space-y-1 text-xs">
                          Shirt number (blank if unknown)
                          <Input
                            value={entry.shirt_number ?? ''}
                            helpLabel="Configured jersey shirt number"
                            help="Keep the explicit number, including 0 or leading zeros. Leave blank only when unknown."
                            inputMode="numeric"
                            maxLength={3}
                            onChange={(e) =>
                              updateToken(index, { shirt_number: e.target.value || null })
                            }
                          />
                        </label>
                      </>
                    )}
                  </>
                )}
              </div>
              <label className="flex min-h-11 items-center gap-2 text-sm">
                <Input
                  type="checkbox"
                  checked={entry.active !== false}
                  onChange={(e) => updateToken(index, { active: e.target.checked })}
                  helpLabel={`Active ${kind} mapping`}
                />
                Active
              </label>
              <button
                type="button"
                className="min-h-11 text-xs text-muted-foreground underline"
                onClick={() =>
                  onChange({
                    ...config,
                    tokens: {
                      ...config.tokens,
                      [kind]: config.tokens[kind].filter((_, i) => i !== index),
                    },
                  })
                }
              >
                Remove from this version (issued tokens stay reserved)
              </button>
            </div>
          ))}
          <button
            type="button"
            className="min-h-11 text-sm text-primary"
            onClick={() =>
              onChange({
                ...config,
                tokens: {
                  ...config.tokens,
                  [kind]: [
                    ...config.tokens[kind],
                    { key: crypto.randomUUID(), label: '', token: '', aliases: [], active: true },
                  ],
                },
              })
            }
          >
            + Add {kind} mapping
          </button>
          <p className="break-words text-xs text-muted-foreground">
            SKU order: product code · period · collection · kit · sleeve · print · edition · fit ·
            collar · audience · colour · size. Save draft, review, then publish using the existing
            controls.
          </p>
        </>
      )}
    </section>
  );
}
